// The ship sim's own state, as one serialisable object.
//
// Imports nothing but the clock's anchor and two names from the heat budget,
// and neither of those files imports anything at all: no three.js, no DOM, no
// `main.ts`. A live `Rng`, a `FixedStep` and every scene handle stay where
// they are; what lands here is the number each was built from, so the whole
// tree survives JSON.stringify and runs under the node test runner. `main.ts`
// reads it through `gameState()` and projects it onto the scene; nothing here
// reaches back. A captain's own runtime is the one exception to "the number
// each was built from": `CaptainRuntimeState` below mirrors the shape
// `src/captain/registry.ts`'s `CapturedCaptainState` hands back, by hand and
// field-for-field rather than by importing it, since that file pulls in
// three.js and this one must not.
//
// The projection runs both ways: `load()` writes the state onto the scene and
// `capture()` folds the scene back into it, so a `toJSON` taken mid-session is
// the game as it stands. `src/state/save.ts` is the slot the two feed.

import { HEAT_INITIAL, type HeatState } from '../scene/heat.ts';
import { MISSION_ANCHOR_T } from '../world/clock.ts';
import { REVEAL_ORDER, type RevealState } from '../sensorReveal.ts';

/** Bumped whenever a field changes meaning or leaves. `fromJSON` refuses anything else. */
export const STATE_VERSION = 8;

/** The oldest version `fromJSON` still reads; see there. */
const OLDEST_VERSION = 6;

/** The hull a save from before the player could pick one flew (`src/cruise/playerShip.ts`). */
export const DEFAULT_PLAYER_SHIP = 'tower';

export interface Vec3State {
  x: number;
  y: number;
  z: number;
}

export interface QuatState {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** A page-driven hold on the controls: the three axes and the throttle with them. */
export interface FlightHold {
  pitch: number;
  yaw: number;
  roll: number;
  throttle: number;
}

/** The seven fields the naval model flies the hull with. */
export interface NavalState {
  heading: number;
  targetHeading: number;
  velocity: Vec3State;
  forwardSpeed: number;
  throttle: number;
  hetCooldownLeft: number;
  hetStallLeft: number;
}

/**
 * Hand flight, minus `deltaVSpent`: that one counts for the whole session by
 * OWNER DECISION 5, so a reset must not reach it and it is pass two's call
 * where a session-long counter belongs.
 */
export interface FlightSection {
  velocity: Vec3State;
  /** The body the flight frame is adopted against; null is a fresh adoption. */
  frame: string | null;
  held: FlightHold | null;
  assistAim: Vec3State | null;
  /** The flight assist's own switch. Read, never set, outside `setAssistOn`. */
  assistOn: boolean;
  /** The cruise throttle bar's held level, [-1, 1]; W/S override it while pressed. */
  heldCruiseThrottle: number;
  /** The body BURN and the pilot readout are homing on, or null for none. */
  flightTargetId: string | null;
}

/** The chase rig's own numbers: where the camera looks from and how far back. */
export interface ChaseState {
  /** The look-around as drawn, a rotation in the ship's own frame; the identity is on the nose. */
  look: QuatState;
  /** The turn the drag has asked for and the drawn look has not made yet: axis times radians, in the look's own axes. */
  lookPending: Vec3State;
  dist: number;
  distTarget: number;
}

/**
 * Where the ship is, in the only frame that is authoritative: heliocentric
 * ecliptic J2000 metres, plus the bubble the float32 scene is drawn in.
 *
 * `seed` and `basis` come with `world` because bubble-local metres are
 * `basis^-1 (world - seed)` and every seeded bearing in a fight is measured
 * from `seed`; restoring `world` alone would put the ship at the right point
 * in the solar system and at the wrong point in its own scene.
 */
export interface PlaceState {
  world: Vec3State;
  seed: Vec3State;
  basisX: Vec3State;
  basisY: Vec3State;
  basisZ: Vec3State;
}

export interface ShipSection {
  naval: NavalState;
  flight: FlightSection;
  chase: ChaseState;
  /** The ship group's euler rotation, which a reset puts back on zero. */
  attitude: Vec3State;
  /**
   * Null means "wherever the sim already is". A scenario opens where the
   * ship stands - each `apply*Scenario` seeds the bubble around it
   * (`seedFightHere` in main.ts) before it scatters anything - so the
   * restart this state drives must not move the hull, and `initialState()`
   * carries null for that reason. A captured state always carries a place, and a restored save is
   * the one caller that applies it.
   */
  place: PlaceState | null;
}

export interface PlayerSection {
  /** The hull the player flies, a `SHIP_SPECS` key. Written only by `setPlayerShip`. */
  shipSpec: string;
  hull: number;
  eap: number;
  heat: number;
  /** Radiator posture the hull is easing toward, 0 stowed to 1 deployed. */
  deployTarget: number;
  /** Real seconds left on the containment window. */
  maydayLeft: number;
  redAlert: boolean;
  /** `HeatState.sinceDamageS`, the hull-repair wait a cruise save would
   *  otherwise restart. */
  heatSinceDamageS: number;
  /** `src/sim/playerVitals.ts`'s red-line override, pushing the drive past the heat's red line. */
  redLine: boolean;
}

export interface WorldSection {
  /** Seconds from J2000, the instant the game clock stands at. */
  t: number;
  /** An index into WARP_LEVELS. */
  warpPick: number;
  /** The authored map being played, or null for cruise. */
  scenarioId: string | null;
}

/** The FSM memory `src/captain/registry.ts`'s `FsmMemory` carries - mirrored
 *  by hand rather than imported; see the header comment. */
export interface CaptainFsmSnapshot {
  /** One of `CAPTAIN_FSM_STATES` below, which mirrors `CaptainState` in
   *  `src/captain/types.ts`. Kept as `string` here so this file does not
   *  import that union. */
  state: string;
  enteredAt: number;
  jinkSign: number;
  lastHetAt: number;
  committedTargetId: string | null;
  lastSeenHull: number;
}

/** The eight labels `CaptainState` (`src/captain/types.ts`) names. A state
 *  outside this set is refused rather than let through as an unknown string,
 *  the same way an unknown STATE_VERSION is. */
const CAPTAIN_FSM_STATES: ReadonlySet<string> = new Set([
  'idle', 'patrol', 'pursue', 'engage', 'evade', 'het-break', 'disengage', 'dead',
]);

/**
 * One captain's durable state, as `src/captain/registry.ts`'s
 * `captureState()` hands it back. That module names, field by field, which
 * of `CaptainRuntime`'s fields are durable and which are scratch (weapon
 * systems, LLM cadence bookkeeping); this is the durable half, serialized.
 */
export interface CaptainRuntimeState {
  rngDrawCount: number;
  /** Draws taken off plasma's own forked stream - see `CapturedCaptainState`
   *  in `src/captain/registry.ts` for why this travels separately. */
  plasmaRngDrawCount: number;
  /** Seconds left on each mount's cooldown at capture time. */
  cooldowns: { plasma: number; pulse: number; phaser: number; missile: number };
  skill: number;
  fsm: CaptainFsmSnapshot;
  heading: number;
  targetHeading: number;
  throttle: number;
  forwardSpeed: number;
  hetCooldownLeft: number;
  hetStallLeft: number;
  eap: number;
  heat: HeatState;
  deployFraction: number;
  deployTarget: number;
  maxSpeed: number;
  speedAccel: number;
  baseTurnRate: number;
  turnRateAtMax: number;
  attachedAtT: number;
  warmupSec: number;
  warmupExpired: boolean;
  /** This captain's own mounts' shots in flight. */
  shots: MountShots;
  llm: CaptainLlmState;
}

/**
 * What a shot in flight is flying at: the player, a ship by its place in the
 * fight's list, a rock by its seq, or `gone` - the target died mid-flight and
 * the shot flies on the way plasma and pulse already do, at the last position
 * it had a lock on. A `ship` came through the player's lock and a `contact`
 * through a captain's sensors; both mark the module they hit (#859). The kind
 * is kept because it names which wrapper a restored shot homes through.
 */
export interface ShotTarget {
  kind: 'player' | 'ship' | 'contact' | 'rock' | 'gone';
  /** The ship's index in the traffic list or the rock's seq; -1 for the player and for `gone`. */
  index: number;
  /** Set only for `gone`: the position the target was last seen at. */
  position?: Vec3State;
}

/**
 * What `nameShotTarget` in `src/main.ts` names a target as once `sweepDead`
 * has spliced it out from under a bolt still homing on it: a `gone` marker,
 * frozen at the last position the save saw it at. Exactly the old throw
 * condition, kept here rather than at the call site: `alive` still throws -
 * a live target this build has no other name for is a save that would
 * otherwise come back as a phantom bolt hitting nothing - and `force`, the
 * debug-only escape hatch (`?resumedrop=shot-throw`), throws even a dead one,
 * for the control that shows a save refusing a target it cannot name at all.
 */
export function untracedShotTarget(position: Vec3State, alive: boolean, force: boolean): ShotTarget {
  if (alive || force) throw new Error('a shot in flight is aimed at a target a save cannot name');
  return { kind: 'gone', index: -1, position };
}

/** What the fire control held at save time: a ship by name, or a rock by its
 *  seq - a rock carries no name for a save to hold instead. */
export type WeaponLockSnapshot = { kind: 'ship'; name: string } | { kind: 'rock'; seq: number } | null;

/** What a save's `combat.inspected` records: the name every kind carries (a
 *  bare string, in a save from before this field carried more), or that name
 *  plus a rock's own seq - its catalog id can repeat in a big enough field,
 *  and the seq is what a restore tells two same-named rocks apart with. */
export type InspectedSnapshot = { name: string; rockSeq: number | null } | null;

/** One shot in flight, mirroring `ShotState` in `src/weapons/shots.ts` by hand. */
export interface ShotSnapshot {
  position: Vec3State;
  velocity: Vec3State;
  life: number;
  age: number;
  maxLife: number;
  speed: number;
  target: ShotTarget;
}

export interface MountShots {
  plasma: ShotSnapshot[];
  pulse: ShotSnapshot[];
  missile: ShotSnapshot[];
}

const MODULE_STATES: ReadonlySet<string> = new Set(['green', 'yellow', 'red', 'destroyed']);

/** One hull module's damage, as `src/scene/custos.ts`'s `ModuleHit` names it. */
export interface ModuleSnapshot {
  id: string;
  /** One of `ModuleDamageState`'s four labels. */
  state: string;
}

const INTENT_POSTURES: ReadonlySet<string> = new Set([
  'aggressive', 'cautious', 'knife-fight', 'stand-off', 'disengage', 'parley',
]);
const INTENT_RELEASES: ReadonlySet<string> = new Set(['free', 'tight', 'hold']);
const BEAT_TRIGGERS: ReadonlySet<string> = new Set([
  'state-change', 'damage-taken', 'hull-critical', 'commit-attack', 'breaking-off', 'first-contact',
]);
const BEAT_URGENCIES: ReadonlySet<string> = new Set(['cold', 'tense', 'desperate']);

/** The reply a captain's model last gave, mirroring `StrategicIntent` in `src/captain/types.ts`. */
export interface IntentSnapshot {
  posture: string;
  targetPriority: string;
  weaponRelease: string;
  preferredRange: number;
  reasoning: string;
  validUntilTs: number;
  commsBeat: { trigger: string; hint: string; urgency: string } | null;
}

/** A captain's side of its conversation with its model, and when it last asked. */
export interface CaptainLlmState {
  /** A reply was still on its way when the save was taken. `unrestorable()` refuses such a save. */
  awaitingModel: boolean;
  everKicked: boolean;
  lastCycleAt: number;
  lastState: string;
  lastHull: number;
  lastTargetId: string | null;
  recentBeats: string[];
  intent: IntentSnapshot | null;
  intentExpiresAt: number;
}

/** One rock as the fight has left it; its place and size are the seed's. */
export interface RockSnapshot {
  seq: number;
  hull: number;
  dead: boolean;
  rotation: Vec3State;
}

/**
 * What the fight has made of its seed that re-running the seed cannot redraw.
 * Null out of a fight.
 */
export interface LiveFightState {
  /** Draws taken off each of the fight's own streams past its fork. */
  streams: { traffic: number; player: number; playerPlasma: number };
  /** The traffic field's own seconds: a Custos hull's drift reads them, and where a hit lands on it reads the drift. */
  trafficClock: number;
  /** Shots that have left a mount this fight: the player's count and the captains' per system. */
  shotsFired: { player: number; captains: { plasma: number; pulse: number; phaser: number; missile: number } };
  /** The auto-end's memory: a hostile has been in the fight, and the fight-clock second the last one went. */
  hostileSeen: boolean;
  hostileGoneAt: number | null;
  rocks: RockSnapshot[];
  player: {
    /** The whole heat budget: `player.heat` alone restarts the ceiling dwell. */
    heat: HeatState;
    deployFraction: number;
    hullRegenCooldown: number;
    cooldowns: { plasma: number; pulse: number; phaser: number };
    modules: ModuleSnapshot[];
    shots: MountShots;
  };
}

/** One NPC as the save records it: the draws already made, not the stream they came from. */
export interface NpcState {
  name: string;
  hull: number;
  heading: number;
  /** The doctrine the captain system attached, or null for an unattached hull. */
  captain: string | null;
  /**
   * Which attach this captain was, counted from zero per engagement. The
   * registry forks a captain's stream on `captain:<ordinal>:<name>`
   * (`src/captain/registry.ts`), so the ordinal IS the stream: a restore that
   * re-attaches a shorter list, or the same list in another order, hands every
   * captain a stream that is not its own unless it hands the ordinal back.
   * -1 for an unattached hull.
   */
  ordinal: number;
  destroyed: boolean;
  /** This captain's durable runtime, or null for an unattached hull. */
  captainRuntime: CaptainRuntimeState | null;
  position: Vec3State;
  velocity: Vec3State;
  /** The group's euler rotation; `heading` above is its y. */
  rotation: Vec3State;
  /** A wreck's tumble, or null for a live hull. */
  deathSpin: Vec3State | null;
  /** Module damage for a modelled hull, or null for one without modules. */
  modules: ModuleSnapshot[] | null;
}

/**
 * The fight. `seed` rebuilds the whole stream tree through `createRng(seed)`
 * and the same named forks; what it does not rebuild is how far each stream
 * has been drawn, which is why every NPC carries its own outcome instead.
 */
export interface CombatSection {
  seed: number | null;
  /** Where the fight's fixed-step clock stood. A load starts a fresh accumulator at it. */
  stepCount: number;
  /** Where the ship was when the scenario scattered; every seeded bearing is measured from it. */
  seedOrigin: Vec3State;
  npcs: NpcState[];
  /** Named, not held: a `DamageTarget` is a scene handle. A ship is named by
   *  its name; a rock, which carries no name, by its seq. */
  weaponLock: WeaponLockSnapshot;
  inspected: InspectedSnapshot;
  live: LiveFightState | null;
}

export interface ProgressionSection {
  /** Contacts whose first-lock hail has already fired. Sorted, so order never moves the JSON. */
  firstLocked: string[];
  /**
   * Story beats already fired, by the ids `src/mission/beats.ts` names.
   * Sorted for the same reason. Empty is both beats armed, which is what a
   * scenario restart means.
   */
  beats: string[];
  /**
   * What the bridge's sensors have earned on each contact that has climbed
   * past its start (`src/sensorReveal.ts`), keyed by ship name in sorted
   * order. Empty is every contact at its profile's start.
   */
  revealed: Record<string, RevealState>;
}

export interface GameState {
  version: number;
  ship: ShipSection;
  player: PlayerSection;
  world: WorldSection;
  combat: CombatSection;
  progression: ProgressionSection;
}

// The numbers a scenario opens on live here and nowhere else, so the reset and
// the state it resets to cannot drift apart.

/** Throttle a scenario opens on, world units a second: a gentle cruise, not dead stop. */
export const INITIAL_THROTTLE = 5.0;

/** Where the chase camera stands before anything zooms it. */
export const INITIAL_CHASE_DIST = 18;

/**
 * The containment window, in REAL seconds. It counts real seconds rather than
 * warped sim seconds because it is a pressure timer the player races, and a
 * transfer at an hour a second would otherwise burn all 47 minutes inside the
 * first real minute of it.
 */
export const INITIAL_MAYDAY_SEC = 47 * 60;

/**
 * The pool and the hull a restart opens on. This is the side that is read:
 * `src/sim/playerVitals.ts` has a setter for each and applies whatever the
 * state says, so a restart and a restored save take the same path.
 */
export const INITIAL_EAP = 92;
export const INITIAL_HULL = 1.0;

/** The state a scenario restart opens on. */
export function initialState(): GameState {
  return {
    version: STATE_VERSION,
    ship: {
      naval: {
        heading: 0,
        targetHeading: 0,
        velocity: { x: 0, y: 0, z: 0 },
        forwardSpeed: 0,
        throttle: INITIAL_THROTTLE,
        hetCooldownLeft: 0,
        hetStallLeft: 0,
      },
      flight: {
        velocity: { x: 0, y: 0, z: 0 },
        frame: null,
        held: null,
        assistAim: null,
        assistOn: true,
        heldCruiseThrottle: 0,
        flightTargetId: null,
      },
      chase: {
        look: { x: 0, y: 0, z: 0, w: 1 },
        lookPending: { x: 0, y: 0, z: 0 },
        dist: INITIAL_CHASE_DIST,
        distTarget: INITIAL_CHASE_DIST,
      },
      attitude: { x: 0, y: 0, z: 0 },
      place: null,
    },
    player: {
      shipSpec: DEFAULT_PLAYER_SHIP,
      hull: INITIAL_HULL,
      eap: INITIAL_EAP,
      heat: HEAT_INITIAL,
      deployTarget: 1,
      maydayLeft: INITIAL_MAYDAY_SEC,
      redAlert: false,
      heatSinceDamageS: 0,
      redLine: false,
    },
    world: {
      t: MISSION_ANCHOR_T,
      warpPick: 0,
      scenarioId: null,
    },
    combat: {
      seed: null,
      stepCount: 0,
      seedOrigin: { x: 0, y: 0, z: 0 },
      npcs: [],
      weaponLock: null,
      inspected: null,
      live: null,
    },
    progression: {
      firstLocked: [],
      beats: [],
      revealed: {},
    },
  };
}

let current: GameState = initialState();

type DeepReadonly<T> = T extends readonly (infer U)[] ? readonly DeepReadonly<U>[]
  : T extends object ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
  : T;
export type ReadonlyGameState = DeepReadonly<GameState>;

/** The one live state. A function rather than a binding so a load is seen by every reader. */
export function gameState(): ReadonlyGameState {
  return current;
}

/** Swap the whole state. The scene side of a load is the caller's, not this module's. */
export function loadState(next: GameState): void {
  current = next;
}

// ---------- The mutation API ----------
// Callers never write a field. Every write the sim makes has a name here, so a
// binding that moves into the state cannot be assigned from six places again.

export function setMaydayLeft(seconds: number): void {
  current.player.maydayLeft = seconds;
}

export function setRedAlert(on: boolean): void {
  current.player.redAlert = on;
}

/** The hull the player flies, a `SHIP_SPECS` key; the scene swap is the caller's. */
export function setPlayerShip(spec: string): void {
  current.player.shipSpec = spec;
}

/** The warp level the player picked: an index into `WARP_LEVELS`. */
export function setWarpLevel(idx: number): void {
  current.world.warpPick = idx;
}

/** The authored map being played, or null for cruise. */
export function setScenarioId(id: string | null): void {
  current.world.scenarioId = id;
}

/** Where the ship is. Null clears it, which is what an uncaptured state means. */
export function setPlace(next: PlaceState | null): void {
  current.ship.place = next;
}

/** Fold the live scene's own numbers into the state. See `capture()` in `src/main.ts`. */
export function setNaval(next: NavalState): void {
  current.ship.naval = next;
}

export function setAttitude(next: Vec3State): void {
  current.ship.attitude = next;
}

export function setFlight(next: FlightSection): void {
  current.ship.flight = next;
}

export function setVitals(
  hull: number, eap: number, heat: number, deployTarget: number, heatSinceDamageS: number,
  redLine: boolean,
): void {
  current.player.hull = hull;
  current.player.eap = eap;
  current.player.heat = heat;
  current.player.deployTarget = deployTarget;
  current.player.heatSinceDamageS = heatSinceDamageS;
  current.player.redLine = redLine;
}

export function setWorldT(t: number): void {
  current.world.t = t;
}

export function setCombat(next: CombatSection): void {
  current.combat = next;
}

export function setBeats(ids: string[]): void {
  current.progression.beats = [...ids].sort();
}

export function setRevealed(revealed: Readonly<Record<string, RevealState>>): void {
  current.progression.revealed = Object.fromEntries(Object.keys(revealed).sort().map((k) => [k, revealed[k]!]));
}

export function holdFlight(hold: FlightHold | null): void {
  current.ship.flight.held = hold;
}

/** The flight assist's own switch. */
export function setAssistOn(on: boolean): void {
  current.ship.flight.assistOn = on;
}

/** The cruise throttle bar's held level, [-1, 1]. */
export function setHeldCruiseThrottle(throttle: number): void {
  current.ship.flight.heldCruiseThrottle = throttle;
}

/** The body BURN and the pilot readout are homing on, or null for none. */
export function setFlightTargetId(id: string | null): void {
  current.ship.flight.flightTargetId = id;
}

export function setChaseLook(look: QuatState): void {
  current.ship.chase.look = { x: look.x, y: look.y, z: look.z, w: look.w };
}

export function setChaseLookPending(pending: Vec3State): void {
  current.ship.chase.lookPending = { x: pending.x, y: pending.y, z: pending.z };
}

export function setChaseDist(dist: number): void {
  current.ship.chase.dist = dist;
}

export function setChaseDistTarget(dist: number): void {
  current.ship.chase.distTarget = dist;
}

/** Record a contact's first lock. True when this was the first, which is the caller's branch. */
export function noteFirstLock(name: string): boolean {
  const locks = current.progression.firstLocked;
  if (locks.includes(name)) return false;
  locks.push(name);
  locks.sort();
  return true;
}

// ---------- Serialisation ----------

export function toJSON(s: GameState): string {
  return JSON.stringify(s);
}

/**
 * Read a state back. Refuses an unknown version by name, and refuses any field
 * that is missing or of the wrong type, naming its path.
 *
 * Versions 7 and 6 still read, fight or cruise. What version 8 added is
 * `player.shipSpec`, and a save from before it reads as the hull every save
 * then flew, `DEFAULT_PLAYER_SHIP`. What version 7 changed is the chase
 * look-around, a yaw and a pitch in version 6 and a free rotation since, and
 * a version 6 save's look reads as settled on the yaw and pitch it was easing
 * toward.
 */
export function fromJSON(text: string): GameState {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    throw new Error(`game state is not JSON: ${(e as Error).message}`);
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new Error('game state is not an object');
  }
  const root = raw as Record<string, unknown>;
  const version = num(root, 'version', 'version');
  if (!Number.isInteger(version) || version < OLDEST_VERSION || version > STATE_VERSION) {
    throw new Error(`game state version ${version} cannot be read by version ${STATE_VERSION}`);
  }
  const previous = version === 6;
  const pickedShip = version >= 8;
  const ship = obj(root['ship'], 'ship');
  const player = obj(root['player'], 'player');
  const world = obj(root['world'], 'world');
  const combat = obj(root['combat'], 'combat');
  const progression = obj(root['progression'], 'progression');
  const naval = obj(ship['naval'], 'ship.naval');
  const flight = obj(ship['flight'], 'ship.flight');
  const chase = obj(ship['chase'], 'ship.chase');
  const seed = combat['seed'] === null ? null : num(combat, 'seed', 'combat.seed');
  return {
    version: STATE_VERSION,
    ship: {
      naval: {
        heading: num(naval, 'heading', 'ship.naval.heading'),
        targetHeading: num(naval, 'targetHeading', 'ship.naval.targetHeading'),
        velocity: vec3(naval['velocity'], 'ship.naval.velocity'),
        forwardSpeed: num(naval, 'forwardSpeed', 'ship.naval.forwardSpeed'),
        throttle: num(naval, 'throttle', 'ship.naval.throttle'),
        hetCooldownLeft: num(naval, 'hetCooldownLeft', 'ship.naval.hetCooldownLeft'),
        hetStallLeft: num(naval, 'hetStallLeft', 'ship.naval.hetStallLeft'),
      },
      flight: {
        velocity: vec3(flight['velocity'], 'ship.flight.velocity'),
        frame: strOrNull(flight, 'frame', 'ship.flight.frame'),
        held: hold(flight['held'], 'ship.flight.held'),
        assistAim: flight['assistAim'] === null
          ? null
          : vec3(flight['assistAim'], 'ship.flight.assistAim'),
        assistOn: bool(flight, 'assistOn', 'ship.flight.assistOn'),
        heldCruiseThrottle: num(flight, 'heldCruiseThrottle', 'ship.flight.heldCruiseThrottle'),
        flightTargetId: strOrNull(flight, 'flightTargetId', 'ship.flight.flightTargetId'),
      },
      chase: {
        look: previous
          ? yawPitchLook(num(chase, 'yawTarget', 'ship.chase.yawTarget'),
            num(chase, 'pitchTarget', 'ship.chase.pitchTarget'))
          : quat(chase['look'], 'ship.chase.look'),
        lookPending: previous ? { x: 0, y: 0, z: 0 } : vec3(chase['lookPending'], 'ship.chase.lookPending'),
        dist: num(chase, 'dist', 'ship.chase.dist'),
        distTarget: num(chase, 'distTarget', 'ship.chase.distTarget'),
      },
      attitude: vec3(ship['attitude'], 'ship.attitude'),
      place: place(ship['place'], 'ship.place'),
    },
    player: {
      shipSpec: pickedShip ? str(player, 'shipSpec', 'player.shipSpec') : DEFAULT_PLAYER_SHIP,
      hull: num(player, 'hull', 'player.hull'),
      eap: num(player, 'eap', 'player.eap'),
      heat: num(player, 'heat', 'player.heat'),
      deployTarget: num(player, 'deployTarget', 'player.deployTarget'),
      maydayLeft: num(player, 'maydayLeft', 'player.maydayLeft'),
      redAlert: bool(player, 'redAlert', 'player.redAlert'),
      heatSinceDamageS: num(player, 'heatSinceDamageS', 'player.heatSinceDamageS'),
      redLine: bool(player, 'redLine', 'player.redLine'),
    },
    world: {
      t: num(world, 't', 'world.t'),
      warpPick: num(world, 'warpPick', 'world.warpPick'),
      scenarioId: strOrNull(world, 'scenarioId', 'world.scenarioId'),
    },
    combat: {
      seed,
      stepCount: num(combat, 'stepCount', 'combat.stepCount'),
      seedOrigin: vec3(combat['seedOrigin'], 'combat.seedOrigin'),
      npcs: list(combat['npcs'], 'combat.npcs').map((n, i) => npcState(n, `combat.npcs[${i}]`)),
      weaponLock: weaponLockSnapshot(combat['weaponLock'], 'combat.weaponLock'),
      inspected: inspectedSnapshot(combat['inspected'], 'combat.inspected'),
      live: liveFight(combat['live'], 'combat.live'),
    },
    progression: {
      firstLocked: names(progression['firstLocked'], 'progression.firstLocked'),
      beats: names(progression['beats'], 'progression.beats'),
      revealed: revealedMap(progression['revealed'], 'progression.revealed'),
    },
  };
}

// ---------- Checked readers ----------
// Each names the path it refused, so a dropped field reads as one line rather
// than as an undefined three modules downstream.

function obj(v: unknown, path: string): Record<string, unknown> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) {
    throw new Error(`game state ${path} is not an object`);
  }
  return v as Record<string, unknown>;
}

function list(v: unknown, path: string): unknown[] {
  if (!Array.isArray(v)) throw new Error(`game state ${path} is not an array`);
  return v;
}

function num(o: Record<string, unknown>, key: string, path: string): number {
  if (!(key in o)) throw new Error(`game state ${path} is missing`);
  const v = o[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new Error(`game state ${path} is not a finite number`);
  }
  return v;
}

function bool(o: Record<string, unknown>, key: string, path: string): boolean {
  if (!(key in o)) throw new Error(`game state ${path} is missing`);
  const v = o[key];
  if (typeof v !== 'boolean') throw new Error(`game state ${path} is not a boolean`);
  return v;
}

function str(o: Record<string, unknown>, key: string, path: string): string {
  if (!(key in o)) throw new Error(`game state ${path} is missing`);
  const v = o[key];
  if (typeof v !== 'string') throw new Error(`game state ${path} is not a string`);
  return v;
}

function strOrNull(o: Record<string, unknown>, key: string, path: string): string | null {
  if (!(key in o)) throw new Error(`game state ${path} is missing`);
  const v = o[key];
  if (v === null) return null;
  if (typeof v !== 'string') throw new Error(`game state ${path} is not a string or null`);
  return v;
}

function numOrNull(o: Record<string, unknown>, key: string, path: string): number | null {
  if (!(key in o)) throw new Error(`game state ${path} is missing`);
  const v = o[key];
  if (v === null) return null;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new Error(`game state ${path} is not a finite number or null`);
  }
  return v;
}

function vec3(v: unknown, path: string): Vec3State {
  const o = obj(v, path);
  return {
    x: num(o, 'x', `${path}.x`),
    y: num(o, 'y', `${path}.y`),
    z: num(o, 'z', `${path}.z`),
  };
}

function quat(v: unknown, path: string): QuatState {
  const o = obj(v, path);
  return {
    x: num(o, 'x', `${path}.x`),
    y: num(o, 'y', `${path}.y`),
    z: num(o, 'z', `${path}.z`),
    w: num(o, 'w', `${path}.w`),
  };
}

/** Version 6's look-around, a yaw about the ship's up then a pitch about the turned right, as the one rotation. */
function yawPitchLook(yaw: number, pitch: number): QuatState {
  const sy = Math.sin(yaw / 2);
  const cy = Math.cos(yaw / 2);
  const sp = Math.sin(pitch / 2);
  const cp = Math.cos(pitch / 2);
  return { x: cy * sp, y: sy * cp, z: -sy * sp, w: cy * cp };
}

function revealedMap(v: unknown, path: string): Record<string, RevealState> {
  const o = obj(v, path);
  return Object.fromEntries(Object.entries(o).map(([name, state]) => {
    if (!REVEAL_ORDER.includes(state as RevealState)) {
      throw new Error(`game state ${path}.${name} is not one of ${REVEAL_ORDER.join(', ')}`);
    }
    return [name, state as RevealState];
  }));
}

function names(v: unknown, path: string): string[] {
  return list(v, path).map((n, i) => {
    if (typeof n !== 'string') throw new Error(`game state ${path}[${i}] is not a string`);
    return n;
  });
}

function place(v: unknown, path: string): PlaceState | null {
  if (v === null) return null;
  const o = obj(v, path);
  return {
    world: vec3(o['world'], `${path}.world`),
    seed: vec3(o['seed'], `${path}.seed`),
    basisX: vec3(o['basisX'], `${path}.basisX`),
    basisY: vec3(o['basisY'], `${path}.basisY`),
    basisZ: vec3(o['basisZ'], `${path}.basisZ`),
  };
}

function hold(v: unknown, path: string): FlightHold | null {
  if (v === null) return null;
  const o = obj(v, path);
  return {
    pitch: num(o, 'pitch', `${path}.pitch`),
    yaw: num(o, 'yaw', `${path}.yaw`),
    roll: num(o, 'roll', `${path}.roll`),
    throttle: num(o, 'throttle', `${path}.throttle`),
  };
}

function captainFsmSnapshot(v: unknown, path: string): CaptainFsmSnapshot {
  const o = obj(v, path);
  const state = str(o, 'state', `${path}.state`);
  if (!CAPTAIN_FSM_STATES.has(state)) {
    throw new Error(`game state ${path}.state is not a captain FSM state`);
  }
  return {
    state,
    enteredAt: num(o, 'enteredAt', `${path}.enteredAt`),
    jinkSign: num(o, 'jinkSign', `${path}.jinkSign`),
    lastHetAt: num(o, 'lastHetAt', `${path}.lastHetAt`),
    committedTargetId: strOrNull(o, 'committedTargetId', `${path}.committedTargetId`),
    lastSeenHull: num(o, 'lastSeenHull', `${path}.lastSeenHull`),
  };
}

function captainHeatSnapshot(v: unknown, path: string): HeatState {
  const o = obj(v, path);
  return {
    heat: num(o, 'heat', `${path}.heat`),
    panelsPerSide: num(o, 'panelsPerSide', `${path}.panelsPerSide`),
    rung: num(o, 'rung', `${path}.rung`),
    sinceDamageS: num(o, 'sinceDamageS', `${path}.sinceDamageS`),
    inRate: num(o, 'inRate', `${path}.inRate`),
    outRate: num(o, 'outRate', `${path}.outRate`),
    netRate: num(o, 'netRate', `${path}.netRate`),
  };
}

function captainCooldowns(v: unknown, path: string): CaptainRuntimeState['cooldowns'] {
  const o = obj(v, path);
  return {
    plasma: num(o, 'plasma', `${path}.plasma`),
    pulse: num(o, 'pulse', `${path}.pulse`),
    phaser: num(o, 'phaser', `${path}.phaser`),
    missile: num(o, 'missile', `${path}.missile`),
  };
}

function captainRuntime(v: unknown, path: string): CaptainRuntimeState | null {
  if (v === null) return null;
  const o = obj(v, path);
  return {
    rngDrawCount: num(o, 'rngDrawCount', `${path}.rngDrawCount`),
    plasmaRngDrawCount: num(o, 'plasmaRngDrawCount', `${path}.plasmaRngDrawCount`),
    cooldowns: captainCooldowns(o['cooldowns'], `${path}.cooldowns`),
    skill: num(o, 'skill', `${path}.skill`),
    fsm: captainFsmSnapshot(o['fsm'], `${path}.fsm`),
    heading: num(o, 'heading', `${path}.heading`),
    targetHeading: num(o, 'targetHeading', `${path}.targetHeading`),
    throttle: num(o, 'throttle', `${path}.throttle`),
    forwardSpeed: num(o, 'forwardSpeed', `${path}.forwardSpeed`),
    hetCooldownLeft: num(o, 'hetCooldownLeft', `${path}.hetCooldownLeft`),
    hetStallLeft: num(o, 'hetStallLeft', `${path}.hetStallLeft`),
    eap: num(o, 'eap', `${path}.eap`),
    heat: captainHeatSnapshot(o['heat'], `${path}.heat`),
    deployFraction: num(o, 'deployFraction', `${path}.deployFraction`),
    deployTarget: num(o, 'deployTarget', `${path}.deployTarget`),
    maxSpeed: num(o, 'maxSpeed', `${path}.maxSpeed`),
    speedAccel: num(o, 'speedAccel', `${path}.speedAccel`),
    baseTurnRate: num(o, 'baseTurnRate', `${path}.baseTurnRate`),
    turnRateAtMax: num(o, 'turnRateAtMax', `${path}.turnRateAtMax`),
    attachedAtT: num(o, 'attachedAtT', `${path}.attachedAtT`),
    warmupSec: num(o, 'warmupSec', `${path}.warmupSec`),
    warmupExpired: bool(o, 'warmupExpired', `${path}.warmupExpired`),
    shots: mountShots(o['shots'], `${path}.shots`),
    llm: captainLlm(o['llm'], `${path}.llm`),
  };
}

function npcState(v: unknown, path: string): NpcState {
  const o = obj(v, path);
  return {
    name: str(o, 'name', `${path}.name`),
    hull: num(o, 'hull', `${path}.hull`),
    heading: num(o, 'heading', `${path}.heading`),
    captain: strOrNull(o, 'captain', `${path}.captain`),
    ordinal: num(o, 'ordinal', `${path}.ordinal`),
    destroyed: bool(o, 'destroyed', `${path}.destroyed`),
    captainRuntime: captainRuntime(o['captainRuntime'], `${path}.captainRuntime`),
    position: vec3(o['position'], `${path}.position`),
    velocity: vec3(o['velocity'], `${path}.velocity`),
    rotation: vec3(o['rotation'], `${path}.rotation`),
    deathSpin: o['deathSpin'] === null ? null : vec3(o['deathSpin'], `${path}.deathSpin`),
    modules: o['modules'] === null ? null : modules(o['modules'], `${path}.modules`),
  };
}

/** A string that has to be one of `allowed`, refused by name otherwise. */
function oneOf(o: Record<string, unknown>, key: string, path: string, allowed: ReadonlySet<string>): string {
  const v = str(o, key, path);
  if (!allowed.has(v)) throw new Error(`game state ${path} is not one of ${[...allowed].join(', ')}`);
  return v;
}

function modules(v: unknown, path: string): ModuleSnapshot[] {
  return list(v, path).map((m, i) => {
    const o = obj(m, `${path}[${i}]`);
    return {
      id: str(o, 'id', `${path}[${i}].id`),
      state: oneOf(o, 'state', `${path}[${i}].state`, MODULE_STATES),
    };
  });
}

function weaponLockSnapshot(v: unknown, path: string): WeaponLockSnapshot {
  if (v === null) return null;
  const o = obj(v, path);
  const kind = str(o, 'kind', `${path}.kind`);
  if (kind === 'ship') return { kind, name: str(o, 'name', `${path}.name`) };
  if (kind === 'rock') return { kind, seq: num(o, 'seq', `${path}.seq`) };
  throw new Error(`game state ${path}.kind is not ship or rock`);
}

/** A bare string is a save from before `inspected` carried a rock's seq -
 *  read as a name with no seq, the same restore-by-name a load already
 *  falls back to once a seq does not resolve. */
function inspectedSnapshot(v: unknown, path: string): InspectedSnapshot {
  if (v === null) return null;
  if (typeof v === 'string') return { name: v, rockSeq: null };
  const o = obj(v, path);
  return { name: str(o, 'name', `${path}.name`), rockSeq: numOrNull(o, 'rockSeq', `${path}.rockSeq`) };
}

function shotTarget(v: unknown, path: string): ShotTarget {
  const o = obj(v, path);
  const kind = str(o, 'kind', `${path}.kind`);
  if (kind !== 'player' && kind !== 'ship' && kind !== 'contact' && kind !== 'rock' && kind !== 'gone') {
    throw new Error(`game state ${path}.kind is not player, ship, contact, rock or gone`);
  }
  if (kind === 'gone') return { kind, index: -1, position: vec3(o['position'], `${path}.position`) };
  return { kind, index: num(o, 'index', `${path}.index`) };
}

function shots(v: unknown, path: string): ShotSnapshot[] {
  return list(v, path).map((sh, i) => {
    const p = `${path}[${i}]`;
    const o = obj(sh, p);
    return {
      position: vec3(o['position'], `${p}.position`),
      velocity: vec3(o['velocity'], `${p}.velocity`),
      life: num(o, 'life', `${p}.life`),
      age: num(o, 'age', `${p}.age`),
      maxLife: num(o, 'maxLife', `${p}.maxLife`),
      speed: num(o, 'speed', `${p}.speed`),
      target: shotTarget(o['target'], `${p}.target`),
    };
  });
}

function mountShots(v: unknown, path: string): MountShots {
  const o = obj(v, path);
  return {
    plasma: shots(o['plasma'], `${path}.plasma`),
    pulse: shots(o['pulse'], `${path}.pulse`),
    missile: shots(o['missile'], `${path}.missile`),
  };
}

function intent(v: unknown, path: string): IntentSnapshot | null {
  if (v === null) return null;
  const o = obj(v, path);
  let commsBeat: IntentSnapshot['commsBeat'] = null;
  if (o['commsBeat'] !== null) {
    const b = obj(o['commsBeat'], `${path}.commsBeat`);
    commsBeat = {
      trigger: oneOf(b, 'trigger', `${path}.commsBeat.trigger`, BEAT_TRIGGERS),
      hint: str(b, 'hint', `${path}.commsBeat.hint`),
      urgency: oneOf(b, 'urgency', `${path}.commsBeat.urgency`, BEAT_URGENCIES),
    };
  }
  return {
    posture: oneOf(o, 'posture', `${path}.posture`, INTENT_POSTURES),
    targetPriority: str(o, 'targetPriority', `${path}.targetPriority`),
    weaponRelease: oneOf(o, 'weaponRelease', `${path}.weaponRelease`, INTENT_RELEASES),
    preferredRange: num(o, 'preferredRange', `${path}.preferredRange`),
    reasoning: str(o, 'reasoning', `${path}.reasoning`),
    validUntilTs: num(o, 'validUntilTs', `${path}.validUntilTs`),
    commsBeat,
  };
}

function captainLlm(v: unknown, path: string): CaptainLlmState {
  const o = obj(v, path);
  return {
    awaitingModel: bool(o, 'awaitingModel', `${path}.awaitingModel`),
    everKicked: bool(o, 'everKicked', `${path}.everKicked`),
    lastCycleAt: num(o, 'lastCycleAt', `${path}.lastCycleAt`),
    lastState: oneOf(o, 'lastState', `${path}.lastState`, CAPTAIN_FSM_STATES),
    lastHull: num(o, 'lastHull', `${path}.lastHull`),
    lastTargetId: strOrNull(o, 'lastTargetId', `${path}.lastTargetId`),
    recentBeats: names(o['recentBeats'], `${path}.recentBeats`),
    intent: intent(o['intent'], `${path}.intent`),
    intentExpiresAt: num(o, 'intentExpiresAt', `${path}.intentExpiresAt`),
  };
}

function liveFight(v: unknown, path: string): LiveFightState | null {
  if (v === null) return null;
  const o = obj(v, path);
  const streams = obj(o['streams'], `${path}.streams`);
  const fired = obj(o['shotsFired'], `${path}.shotsFired`);
  const captains = obj(fired['captains'], `${path}.shotsFired.captains`);
  const player = obj(o['player'], `${path}.player`);
  const cooldowns = obj(player['cooldowns'], `${path}.player.cooldowns`);
  return {
    streams: {
      traffic: num(streams, 'traffic', `${path}.streams.traffic`),
      player: num(streams, 'player', `${path}.streams.player`),
      playerPlasma: num(streams, 'playerPlasma', `${path}.streams.playerPlasma`),
    },
    trafficClock: num(o, 'trafficClock', `${path}.trafficClock`),
    shotsFired: {
      player: num(fired, 'player', `${path}.shotsFired.player`),
      captains: {
        plasma: num(captains, 'plasma', `${path}.shotsFired.captains.plasma`),
        pulse: num(captains, 'pulse', `${path}.shotsFired.captains.pulse`),
        phaser: num(captains, 'phaser', `${path}.shotsFired.captains.phaser`),
        missile: num(captains, 'missile', `${path}.shotsFired.captains.missile`),
      },
    },
    hostileSeen: bool(o, 'hostileSeen', `${path}.hostileSeen`),
    hostileGoneAt: o['hostileGoneAt'] === null ? null : num(o, 'hostileGoneAt', `${path}.hostileGoneAt`),
    rocks: list(o['rocks'], `${path}.rocks`).map((r, i) => {
      const ro = obj(r, `${path}.rocks[${i}]`);
      return {
        seq: num(ro, 'seq', `${path}.rocks[${i}].seq`),
        hull: num(ro, 'hull', `${path}.rocks[${i}].hull`),
        dead: bool(ro, 'dead', `${path}.rocks[${i}].dead`),
        rotation: vec3(ro['rotation'], `${path}.rocks[${i}].rotation`),
      };
    }),
    player: {
      heat: captainHeatSnapshot(player['heat'], `${path}.player.heat`),
      deployFraction: num(player, 'deployFraction', `${path}.player.deployFraction`),
      hullRegenCooldown: num(player, 'hullRegenCooldown', `${path}.player.hullRegenCooldown`),
      cooldowns: {
        plasma: num(cooldowns, 'plasma', `${path}.player.cooldowns.plasma`),
        pulse: num(cooldowns, 'pulse', `${path}.player.cooldowns.pulse`),
        phaser: num(cooldowns, 'phaser', `${path}.player.cooldowns.phaser`),
      },
      modules: modules(player['modules'], `${path}.player.modules`),
      shots: mountShots(player['shots'], `${path}.player.shots`),
    },
  };
}
